// ============================================================
// Picking things out of a list, with a mouse.
//
// ONE GRAMMAR, used by both tabs of the character menu. They had two -
// the Folder tab replaced the selection on a plain click while the
// session list did the same but only added with ctrl - so you learnt
// "click replaces" in one and would have learnt "click adds" in the
// other. Two grammars in one menu is a trap (v8.24.0).
//
//   click        add to the selection, or take it out again
//   right-click  take it out, always
//   shift+click  everything between the last click and this one
//
// Pure, so the range walk and the anchor bookkeeping - the two parts
// that are actually fiddly - can be proved without a DOM.
// ============================================================

export interface SelectionInput {
  /** Every selectable path, in the order they are drawn. Ranges walk this. */
  order: string[];
  /** What is picked now. */
  selected: Set<string>;
  /** Where the last plain click landed, for a range to reach back to. */
  anchor: string | null;
}

export interface SelectionClick {
  shift?: boolean;
  /** A right-click, which only ever removes. */
  remove?: boolean;
}

export interface SelectionResult {
  selected: Set<string>;
  anchor: string | null;
}

/** Add everything between two paths, inclusive; false when either is not in the list. */
function addRange(selected: Set<string>, order: string[], anchor: string, path: string): boolean {
  const from = order.indexOf(anchor);
  const to = order.indexOf(path);
  if (from === -1 || to === -1) return false;
  const [lo, hi] = from <= to ? [from, to] : [to, from];
  for (let i = lo; i <= hi; i++) selected.add(order[i]);
  return true;
}

/**
 * Apply one click.
 *
 * THE ANCHOR MOVES ON A PLAIN CLICK AND NOT ON A RANGE, which is what
 * makes shift-clicking twice in a row extend from the same place rather
 * than walking away from it - the behaviour every file manager has.
 */
export function applySelection(input: SelectionInput, path: string, click: SelectionClick = {}): SelectionResult {
  const selected = new Set(input.selected);

  if (click.remove) {
    selected.delete(path);
    // The anchor survives a removal: taking one out of a range should
    // not cost you the place you were extending from.
    return { selected, anchor: input.anchor };
  }

  if (click.shift && input.anchor && addRange(selected, input.order, input.anchor, path)) {
    return { selected, anchor: input.anchor };
  }

  if (selected.has(path)) selected.delete(path);
  else selected.add(path);
  return { selected, anchor: path };
}

/**
 * Every path in a group, added or removed together.
 *
 * Clicking a group name is the bulk gesture people reach for first, and
 * it costs one function: all of them in, unless they are all in already,
 * in which case all of them out.
 */
export function toggleGroupSelection(selected: Set<string>, paths: string[]): Set<string> {
  const next = new Set(selected);
  const allIn = paths.length > 0 && paths.every((p) => next.has(p));
  for (const p of paths) {
    if (allIn) next.delete(p);
    else next.add(p);
  }
  return next;
}
